import { useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'welcome', label: 'Welcome', icon: '🏠' },
  { id: 'roles', label: 'All User Roles', icon: '👥' },
  { id: 'registration', label: 'Registration & Setup', icon: '🔐' },
  { id: 'pipeline', label: 'Lead Pipeline', icon: '🔄' },
  { id: 'glossary', label: 'Quick Glossary', icon: '📖' },
];

const ROLES = [
  {
    id: 'admin',
    label: 'Admin',
    icon: '👑',
    color: 'from-violet-600 to-purple-700',
    border: 'border-violet-500/40',
    badge: 'bg-violet-500/20 text-violet-300',
    dot: 'bg-violet-400',
    description: 'Has full control over the entire system. Can create users, configure settings, view all data, and override any decision.',
    canDo: [
      'Create and manage all user accounts',
      'View and modify every lead in the system',
      'Assign any lead to any CC agent',
      'Override doctor rejections',
      'Assign hospitals to leads',
      'Generate payment and payout records',
      'Access system logs and activity history',
      'Manage MOU templates and onboarding',
      'Export reports and data',
    ],
    howRegistered: 'Created directly in the database during initial system setup. Only one or two admins should exist.',
    loginEmail: 'admin@doxez.in',
  },
  {
    id: 'cc_head',
    label: 'CC Head',
    icon: '📊',
    color: 'from-emerald-600 to-teal-700',
    border: 'border-emerald-500/40',
    badge: 'bg-emerald-500/20 text-emerald-300',
    dot: 'bg-emerald-400',
    description: 'Manager of the Customer Care team. Oversees all CC agents, handles escalations, reviews rejected cases, and assigns hospitals.',
    canDo: [
      'View all leads across the entire CC team',
      'Reassign leads between CC agents',
      'Handle doctor-rejected cases (reassign or close)',
      'Assign hospitals (when admin is unavailable)',
      'Trigger payout after post-op monitoring approval',
      'View team performance reports',
      'Escalate stuck cases to admin',
      'Monitor stale leads across all agents',
    ],
    howRegistered: 'Created by Admin via the User Management section. Role is set to cc_head.',
    loginEmail: 'cc_head@doxez.in',
  },
  {
    id: 'customer_care',
    label: 'Customer Care (CC)',
    icon: '🎧',
    color: 'from-cyan-600 to-blue-700',
    border: 'border-cyan-500/40',
    badge: 'bg-cyan-500/20 text-cyan-300',
    dot: 'bg-cyan-400',
    description: 'The front-line agent. Directly handles patient communication, creates leads, screens patients, uploads documents, and does post-op monitoring.',
    canDo: [
      'Create new leads from calls or walk-ins',
      'Call and screen patients (eligibility, willingness)',
      'Fill complete medical history forms',
      'Upload patient documents to AWS S3',
      'Schedule and track follow-ups',
      'Send WhatsApp notifications to patients',
      'Track payment status and remind patients',
      'Add post-op monitoring log entries',
      'Mark monitoring complete to trigger payout',
    ],
    howRegistered: 'Created by Admin via the User Management section. Role is set to customer_care.',
    loginEmail: 'agent@doxez.in',
  },
  {
    id: 'doctor',
    label: 'Doctor',
    icon: '🩺',
    color: 'from-blue-600 to-indigo-700',
    border: 'border-blue-500/40',
    badge: 'bg-blue-500/20 text-blue-300',
    dot: 'bg-blue-400',
    description: 'Receives assigned patient cases, reviews medical records, approves or rejects surgery cases, and verifies surgery via OTP.',
    canDo: [
      'View all leads assigned to them',
      'Review patient documents and medical history',
      'Approve or reject a patient case',
      'Verify surgery start via OTP (OTP 1)',
      'Verify surgery completion via OTP (OTP 2)',
      'Set their own availability slots',
      'View their payout history',
    ],
    howRegistered: 'Admin sends an onboarding invitation email. Doctor clicks the link, fills the onboarding form (personal info, license, bank details, signature), then Admin verifies.',
    loginEmail: 'doctor@doxez.in',
  },
  {
    id: 'hospital',
    label: 'Hospital',
    icon: '🏥',
    color: 'from-rose-600 to-pink-700',
    border: 'border-rose-500/40',
    badge: 'bg-rose-500/20 text-rose-300',
    dot: 'bg-rose-400',
    description: 'Partner hospital that hosts the surgeries. Views their facility\'s scheduled surgeries and tracks case statuses.',
    canDo: [
      'View surgeries assigned to their hospital',
      'Track surgery status and schedules',
      'View payout history for their hospital',
      'Download surgery summary reports',
    ],
    howRegistered: 'Admin sends an onboarding invitation email. Hospital representative fills the onboarding form (hospital details, bank details, documents), then Admin verifies.',
    loginEmail: 'hospital@doxez.in',
  },
  {
    id: 'accountant',
    label: 'Accountant',
    icon: '💼',
    color: 'from-amber-600 to-orange-700',
    border: 'border-amber-500/40',
    badge: 'bg-amber-500/20 text-amber-300',
    dot: 'bg-amber-400',
    description: 'Handles all financial operations — generating payment links, processing payouts to doctors, hospitals, and agents.',
    canDo: [
      'Generate Razorpay payment links for leads',
      'Track payment statuses and verify receipts',
      'Process payouts after surgery completion',
      'Set doctor, hospital, and agent share percentages',
      'Generate PDF payment receipts',
      'View financial reports and revenue dashboards',
      'Handle refunds if needed',
    ],
    howRegistered: 'Created by Admin via the User Management section. Role is set to accountant.',
    loginEmail: 'accountant@doxez.in',
  },
  {
    id: 'marketing_agent',
    label: 'Marketing Agent',
    icon: '📢',
    color: 'from-fuchsia-600 to-pink-700',
    border: 'border-fuchsia-500/40',
    badge: 'bg-fuchsia-500/20 text-fuchsia-300',
    dot: 'bg-fuchsia-400',
    description: 'External agents who refer patients. They earn a commission when their referred lead completes surgery.',
    canDo: [
      'View leads they have referred',
      'Track conversion status of their referrals',
      'View their commission/payout history',
      'Use their unique referral code (DXZ-XXXXXX)',
    ],
    howRegistered: 'Admin creates their profile in Marketing Agents section. A user account is linked to their agent profile.',
    loginEmail: 'agent@doxez.in',
  },
];

const REG_STEPS = [
  {
    role: 'Admin / CC Head / CC Agent / Accountant',
    icon: '⚙️',
    color: 'from-violet-600 to-cyan-600',
    steps: [
      { title: 'Admin logs in', desc: 'Admin accesses the system with their credentials.' },
      { title: 'Go to User Management', desc: 'Navigate to /users in the sidebar.' },
      { title: 'Click "Create User"', desc: 'Fill in name, email, phone, and select role (admin / cc_head / customer_care / accountant).' },
      { title: 'Set a temporary password', desc: 'User is created and can immediately log in at /login.' },
      { title: 'User logs in', desc: 'New user logs in and can access their role-specific dashboard.' },
    ],
  },
  {
    role: 'Doctor / Hospital (Partner Onboarding)',
    icon: '📋',
    color: 'from-rose-600 to-amber-600',
    steps: [
      { title: 'Admin goes to Onboarding section', desc: 'Navigate to /onboarding in the admin sidebar.' },
      { title: 'Select role and enter email', desc: 'Admin selects Doctor or Hospital and enters the email address.' },
      { title: 'System sends invitation email', desc: 'An email is sent with a secure link (valid for 48 hours).' },
      { title: 'Doctor/Hospital clicks the link', desc: 'Opens the onboarding form at /onboarding-form/:token' },
      { title: 'Fill the onboarding form', desc: 'Personal info, specialization, bank details, documents (PAN, Aadhaar, license), and signature.' },
      { title: 'Submit for review', desc: 'Status changes to "submitted". Admin receives a notification.' },
      { title: 'Admin verifies documents', desc: 'Goes to Onboarding Management, reviews all documents.' },
      { title: 'Admin approves', desc: 'Account is activated. Doctor/Hospital can now log in and view their dashboard.' },
    ],
  },
  {
    role: 'Marketing Agent',
    icon: '📢',
    color: 'from-fuchsia-600 to-pink-600',
    steps: [
      { title: 'Admin goes to Marketing Agents', desc: 'Navigate to /marketing-agents in the sidebar.' },
      { title: 'Click "Add Agent"', desc: 'Fill in agent name, phone, email, and commission details.' },
      { title: 'System generates a referral code', desc: 'Auto-generated code like DXZ-7KR4M2 is assigned.' },
      { title: 'Create linked user account', desc: 'A user account is created with role marketing_agent.' },
      { title: 'Agent logs in', desc: 'Agent can view their leads, commission status, and payout history.' },
    ],
  },
];

const PIPELINE_STAGES = [
  {
    status: 'new',
    label: 'Lead Created',
    color: 'from-slate-600 to-slate-700',
    textColor: 'text-slate-300',
    dotColor: 'bg-slate-400',
    borderColor: 'border-slate-500/50',
    icon: '🆕',
    whoActs: 'Customer Care / Admin / Doctor / Hospital / Marketing Agent',
    whatHappens: [
      'Patient inquiry is received (call, walk-in, WhatsApp, website form).',
      'CC agent or Admin creates a new lead in the system.',
      'Patient name, phone, city, disease, and treatment required are filled in.',
      'Source is recorded (admin / doctor / hospital / marketing_agent / DOXEZ / etc.).',
      'Lead type is set: lead_only or surgery.',
      'Priority is set: low / medium / high.',
      'CC agent is assigned to handle this lead.',
    ],
    outcome: 'Lead moves to Screening stage.',
  },
  {
    status: 'screening',
    label: 'Screening',
    color: 'from-blue-600 to-blue-700',
    textColor: 'text-blue-300',
    dotColor: 'bg-blue-400',
    borderColor: 'border-blue-500/50',
    icon: '📋',
    whoActs: 'Customer Care',
    whatHappens: [
      'CC agent calls the patient.',
      'Screening form is filled: eligibility, condition suitability, complications.',
      'Patient willingness to proceed is confirmed.',
      'Timeline is set: Immediate or Later.',
      'If patient is ready → status becomes patient_ready.',
      'If patient is not interested → status becomes not_interested (lead closed permanently).',
      'A follow-up date may be scheduled if the patient needs time.',
    ],
    outcome: 'Patient moves to Document Verification or is marked as not_interested.',
  },
  {
    status: 'doc_verification',
    label: 'Document Upload',
    color: 'from-amber-600 to-orange-700',
    textColor: 'text-amber-300',
    dotColor: 'bg-amber-400',
    borderColor: 'border-amber-500/50',
    icon: '📁',
    whoActs: 'Customer Care',
    whatHappens: [
      'CC agent collects documents from the patient (medical reports, Aadhaar, insurance, scans).',
      'All files are uploaded directly to AWS S3 storage.',
      'Complete medical history is entered: current illness, past diseases, previous surgeries, family history.',
      'Medications are recorded: current medicines, blood thinners, steroids.',
      'Allergies are noted: drug, food, anesthesia.',
      'Vitals are captured: BP, sugar, weight, height, oxygen level.',
      'WhatsApp message sent to patient confirming document receipt.',
      'Doctor receives a notification to begin their review.',
    ],
    outcome: 'Lead moves to Clinical Approval (doctor review).',
  },
  {
    status: 'clinical_approval',
    label: 'Doctor Review',
    color: 'from-indigo-600 to-indigo-700',
    textColor: 'text-indigo-300',
    dotColor: 'bg-indigo-400',
    borderColor: 'border-indigo-500/50',
    icon: '🩺',
    whoActs: 'Assigned Surgeon / Doctor',
    whatHappens: [
      'Doctor receives a notification and opens the assigned case.',
      'Doctor reviews all uploaded documents, vitals, and medical history.',
      'Doctor makes a decision:',
      '  → APPROVE: Status becomes surgery_ready. Lead moves to hospital assignment.',
      '  → REJECT: Status becomes doctor_rejected. CC Head is notified for review.',
      'If rejected, CC Head can reassign to a different doctor or close the case.',
      'Patient is notified of the outcome via WhatsApp.',
    ],
    outcome: 'If approved → Hospital Assignment. If rejected → CC Head review.',
  },
  {
    status: 'hospital_assignment',
    label: 'Hospital Assignment',
    color: 'from-rose-600 to-pink-700',
    textColor: 'text-rose-300',
    dotColor: 'bg-rose-400',
    borderColor: 'border-rose-500/50',
    icon: '🏥',
    whoActs: 'Admin / CC Head',
    whatHappens: [
      'Admin or CC Head selects a suitable hospital for the surgery.',
      'Selection is based on: surgery type, location, bed availability, doctor affiliation.',
      'Hospital is assigned → assignedHospital field is set.',
      'Hospital receives a notification about the incoming case.',
      'Patient is informed of the hospital assignment via CC agent.',
    ],
    outcome: 'Lead moves to Surgery Scheduling.',
  },
  {
    status: 'scheduling',
    label: 'Surgery Scheduling',
    color: 'from-teal-600 to-cyan-700',
    textColor: 'text-teal-300',
    dotColor: 'bg-teal-400',
    borderColor: 'border-teal-500/50',
    icon: '📅',
    whoActs: 'Admin / CC / Doctor',
    whatHappens: [
      'A formal surgery booking is created in the system.',
      'Booking ID is auto-generated: BKG-XXXXXXXX.',
      'Surgery type, name, scheduled date and time are set.',
      'OT room, building, and floor are assigned.',
      'Medical team members are added (surgeon, anesthesiologist, nurses).',
      'Pre-operative checklist items are created.',
      'CC agent confirms the schedule with the patient via call.',
      'Patient receives surgery details via WhatsApp.',
    ],
    outcome: 'Payment link is generated → Lead moves to Payment Pending.',
  },
  {
    status: 'payment_pending',
    label: 'Payment',
    color: 'from-yellow-600 to-amber-700',
    textColor: 'text-yellow-300',
    dotColor: 'bg-yellow-400',
    borderColor: 'border-yellow-500/50',
    icon: '💳',
    whoActs: 'Accountant',
    whatHappens: [
      'Accountant generates a Razorpay payment link for the surgery cost.',
      'Link is sent to the patient via WhatsApp and Email.',
      'Patient makes payment using card, UPI, or net banking.',
      'Razorpay sends a webhook to the backend confirming the payment.',
      'Backend automatically verifies and records the payment.',
      'Lead status becomes paid. Surgery status becomes payment_completed.',
      'All parties (doctor, hospital, CC) receive a real-time notification via Socket.IO.',
      'Payment receipt PDF is auto-generated.',
    ],
    outcome: 'Lead moves to Surgery (OTP verification begins).',
  },
  {
    status: 'surgery',
    label: 'Surgery — OTP Start',
    color: 'from-red-600 to-rose-700',
    textColor: 'text-red-300',
    dotColor: 'bg-red-400',
    borderColor: 'border-red-500/50',
    icon: '⚕️',
    whoActs: 'Surgeon + Anesthesiologist',
    whatHappens: [
      'On surgery day, the system sends individual OTPs to the Surgeon and Anesthesiologist via WhatsApp.',
      'Surgeon verifies their OTP → surgeonVerified: true.',
      'Anesthesiologist verifies their OTP → anesthesiologistVerified: true.',
      'When BOTH are verified → Surgery status: surgery_started.',
      'Lead status becomes surgery. Surgery start timestamp is recorded.',
      'All team members receive a real-time Socket.IO update.',
      'If surgery is aborted:',
      '  → isAborted: true, fault is assigned (Patient / Hospital / Doctor).',
      '  → Penalty percentage is set, penalty payment link is created.',
    ],
    outcome: 'Surgery is underway. Completion OTP is sent when surgery ends.',
  },
  {
    status: 'closure',
    label: 'Surgery Completion + Post-Op',
    color: 'from-purple-600 to-violet-700',
    textColor: 'text-purple-300',
    dotColor: 'bg-purple-400',
    borderColor: 'border-purple-500/50',
    icon: '🔬',
    whoActs: 'Surgeon + CC Agent',
    whatHappens: [
      'Surgery ends. System sends Completion OTPs to Surgeon and Anesthesiologist.',
      'Surgeon verifies completion OTP → surgeonCompletionVerified: true.',
      'Anesthesiologist verifies → anesthesiologistCompletionVerified: true.',
      'Surgery status becomes surgery_completed. End time is recorded.',
      'Lead status becomes closure.',
      'CC agent begins 3-day post-op monitoring:',
      '  Day 1: Call patient, log visit date, time, and notes.',
      '  Day 2: Follow-up call, add log entry.',
      '  Day 3: Final check, mark monitoring as complete.',
      'When monitoring is complete → Accountant is notified to generate payout.',
    ],
    outcome: 'Lead moves to Payout Processing.',
  },
  {
    status: 'payout_processing',
    label: 'Payout Processing',
    color: 'from-green-600 to-emerald-700',
    textColor: 'text-green-300',
    dotColor: 'bg-green-400',
    borderColor: 'border-green-500/50',
    icon: '💰',
    whoActs: 'Accountant',
    whatHappens: [
      'Accountant receives notification that surgery + monitoring is complete.',
      'Accountant opens the payout section and reviews the case.',
      'Total amount is split into shares:',
      '  → Lead Provider Doctor share (referral fee)',
      '  → Surgeon share (surgery fee)',
      '  → Anesthesiologist share',
      '  → Hospital share',
      '  → Marketing Agent commission (if applicable)',
      'Each payout is processed and a Transaction ID is recorded.',
      'PDF receipts are generated for each party.',
      'Doctor, Hospital, and Agent receive WhatsApp notifications with payout confirmation.',
    ],
    outcome: 'All payouts complete → Lead moves to Completed.',
  },
  {
    status: 'completed',
    label: 'Completed',
    color: 'from-emerald-600 to-green-700',
    textColor: 'text-emerald-300',
    dotColor: 'bg-emerald-400',
    borderColor: 'border-emerald-500/50',
    icon: '✅',
    whoActs: 'System (Automatic)',
    whatHappens: [
      'Lead is permanently marked as completed.',
      'Surgery record is marked as closed.',
      'Full case is archived in the Activity Log.',
      'All documents, OTP records, payment and payout details remain accessible for audit.',
      'Admin and CC Head can view this case in reports and performance dashboards.',
      'The complete lead journey — from first contact to deal closure — is recorded.',
    ],
    outcome: 'Deal fully closed. Case archived. 🎉',
  },
];

const GLOSSARY = [
  { term: 'Lead', def: 'A patient inquiry that enters the system. The core unit of the CRM.' },
  { term: 'Booking / Surgery', def: 'A formal surgery record linked to a Lead. Created after Hospital Assignment.' },
  { term: 'Dual Doctor System', def: 'A case can have two doctors — one who referred the patient (Lead Provider) and one who performs the surgery (Surgeon).' },
  { term: 'OTP Verification', def: 'A two-step surgery verification. OTP 1 = Surgery Start. OTP 2 = Surgery Completion. Both surgeon and anesthesiologist must verify.' },
  { term: 'Payout', def: 'Financial settlement after surgery. Splits total revenue among Doctor, Hospital, and Marketing Agent.' },
  { term: 'Webhook', def: 'An automatic HTTP call from Razorpay to our backend when a payment is confirmed.' },
  { term: 'Socket.IO', def: 'Real-time communication layer. All users get instant updates (payment received, surgery started) without refreshing the page.' },
  { term: 'Stale Lead', def: 'A lead stuck on a stage for too long. Detected by a nightly Cron Job. CC agent gets a reminder.' },
  { term: 'Onboarding Token', def: 'A secure, time-limited URL token sent to Doctors/Hospitals for completing their onboarding form.' },
  { term: 'Activity Log', def: 'A complete audit trail of every action taken in the system — who did what and when.' },
  { term: 'Referral Code', def: 'A unique code (DXZ-XXXXXX) assigned to Marketing Agents to track referred leads.' },
  { term: 'patient_ready', def: 'An intermediate status after screening. Indicates the patient is willing and eligible to proceed.' },
  { term: 'surgery_ready', def: 'Status after doctor approval. Means the doctor has cleared the patient for surgery.' },
  { term: 'not_interested', def: 'Terminal status. The patient is no longer interested. Lead is permanently closed.' },
  { term: 'doctor_rejected', def: 'Doctor has rejected the patient case. Goes to CC Head for further action.' },
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function GuidebookUI() {
  const [activeSection, setActiveSection] = useState('welcome');
  const [activeRole, setActiveRole] = useState(null);
  const [activeStage, setActiveStage] = useState(null);
  const [activeRegStep, setActiveRegStep] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex font-sans">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col transition-all duration-300 sticky top-0 h-screen`}>
        {/* Logo */}
        <div className={`p-4 border-b border-slate-800 flex items-center gap-3 ${!sidebarOpen && 'justify-center'}`}>
          <span className="text-2xl shrink-0">🏥</span>
          {sidebarOpen && (
            <div>
              <p className="text-sm font-bold text-white">Doxez CRM</p>
              <p className="text-[10px] text-slate-500">Guidebook</p>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="ml-auto text-slate-500 hover:text-white transition-colors text-xs"
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => { setActiveSection(s.id); setActiveRole(null); setActiveStage(null); setActiveRegStep(null); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${activeSection === s.id
                  ? 'bg-gradient-to-r from-violet-600/30 to-cyan-600/20 border border-violet-500/30 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                }`}
            >
              <span className="text-lg shrink-0">{s.icon}</span>
              {sidebarOpen && <span className="text-sm font-medium">{s.label}</span>}
            </button>
          ))}
        </nav>

        {sidebarOpen && (
          <div className="p-4 border-t border-slate-800">
            <p className="text-[10px] text-slate-600 text-center">Hedena Healthcare · 2026</p>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">

        {/* ── WELCOME ─────────────────────────────────────── */}
        {activeSection === 'welcome' && (
          <div className="max-w-4xl mx-auto p-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-900/60 via-slate-900 to-cyan-900/40 border border-violet-500/20 p-10 mb-8">
              <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
              <div className="relative">
                <div className="text-6xl mb-4">🏥</div>
                <h1 className="text-4xl font-bold text-white mb-3">Welcome to Doxez CRM</h1>
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  This guidebook will walk you through everything you need to know — every user role, how they register,
                  and exactly how a patient's case moves through all 11 stages from first contact to final payout.
                </p>
                <div className="flex flex-wrap gap-3">
                  {SECTIONS.slice(1).map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setActiveSection(s.id)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-sm font-medium transition-all"
                    >
                      <span>{s.icon}</span> {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'User Roles', value: '7', icon: '👥', color: 'from-violet-600 to-purple-700' },
                { label: 'Pipeline Stages', value: '11', icon: '🔄', color: 'from-cyan-600 to-blue-700' },
                { label: 'API Endpoints', value: '27+', icon: '⚡', color: 'from-amber-600 to-orange-700' },
                { label: 'DB Models', value: '22', icon: '🗄️', color: 'from-emerald-600 to-teal-700' },
              ].map((stat) => (
                <div key={stat.label} className={`p-5 rounded-2xl bg-gradient-to-br ${stat.color} text-center`}>
                  <div className="text-3xl mb-1">{stat.icon}</div>
                  <div className="text-3xl font-bold">{stat.value}</div>
                  <div className="text-sm text-white/70 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ALL ROLES ────────────────────────────────────── */}
        {activeSection === 'roles' && (
          <div className="max-w-5xl mx-auto p-8">
            <h2 className="text-2xl font-bold mb-2">👥 All User Roles</h2>
            <p className="text-slate-400 mb-8">Click on any role to see their full capabilities and how they are registered.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {ROLES.map((role) => (
                <button
                  key={role.id}
                  onClick={() => setActiveRole(activeRole === role.id ? null : role.id)}
                  className={`p-5 rounded-2xl border text-left transition-all duration-300 ${activeRole === role.id
                      ? `bg-gradient-to-br ${role.color} border-transparent shadow-2xl`
                      : `bg-slate-800/50 ${role.border} hover:bg-slate-800`
                    }`}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-3xl shrink-0">{role.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-white">{role.label}</h3>
                        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full border ${activeRole === role.id ? 'bg-white/20 border-white/20 text-white' : role.badge}`}>
                          {activeRole === role.id ? 'Expanded ↑' : 'Click to expand ↓'}
                        </span>
                      </div>
                      <p className={`text-sm leading-relaxed ${activeRole === role.id ? 'text-white/80' : 'text-slate-400'}`}>
                        {role.description}
                      </p>
                    </div>
                  </div>

                  {activeRole === role.id && (
                    <div className="mt-5 pt-5 border-t border-white/20 space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">What they can do</h4>
                        <div className="space-y-2">
                          {role.canDo.map((item, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                                <span className="text-[9px]">✓</span>
                              </div>
                              <p className="text-sm text-white/80">{item}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="p-3 rounded-xl bg-black/20 border border-white/10">
                        <h4 className="text-xs font-semibold text-white/50 uppercase mb-1">How they are registered</h4>
                        <p className="text-sm text-white/80">{role.howRegistered}</p>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── REGISTRATION ────────────────────────────────── */}
        {activeSection === 'registration' && (
          <div className="max-w-4xl mx-auto p-8">
            <h2 className="text-2xl font-bold mb-2">🔐 Registration & Setup</h2>
            <p className="text-slate-400 mb-8">Step-by-step guide on how each type of user gets set up in the system.</p>

            <div className="space-y-6">
              {REG_STEPS.map((group, gi) => (
                <div key={gi} className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setActiveRegStep(activeRegStep === gi ? null : gi)}
                    className={`w-full p-5 text-left flex items-center gap-4 transition-all ${activeRegStep === gi ? `bg-gradient-to-r ${group.color}` : 'hover:bg-slate-700/30'}`}
                  >
                    <span className="text-3xl">{group.icon}</span>
                    <div>
                      <h3 className="font-bold text-white">{group.role}</h3>
                      <p className={`text-sm ${activeRegStep === gi ? 'text-white/70' : 'text-slate-400'}`}>
                        {group.steps.length} steps · Click to expand
                      </p>
                    </div>
                    <span className={`ml-auto text-slate-400 text-lg transition-transform duration-200 ${activeRegStep === gi ? 'rotate-90' : ''}`}>›</span>
                  </button>

                  {activeRegStep === gi && (
                    <div className="p-6 pt-2">
                      <div className="relative pl-6 space-y-0">
                        {group.steps.map((step, si) => (
                          <div key={si} className="flex gap-4 pb-6 relative">
                            {/* Line */}
                            {si < group.steps.length - 1 && (
                              <div className="absolute left-0 top-6 bottom-0 w-px bg-slate-700 ml-[11px]" />
                            )}
                            {/* Dot */}
                            <div className="relative z-10 w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shrink-0 text-xs font-bold">
                              {si + 1}
                            </div>
                            {/* Content */}
                            <div className="flex-1 pt-0.5">
                              <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                              <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PIPELINE ────────────────────────────────────── */}
        {activeSection === 'pipeline' && (
          <div className="max-w-5xl mx-auto p-8">
            <h2 className="text-2xl font-bold mb-2">🔄 Lead Pipeline</h2>
            <p className="text-slate-400 mb-6">
              A patient's case goes through <strong className="text-white">11 stages</strong> from the first contact to the final payout.
              Click any stage to see the full detail.
            </p>

            {/* Flow Diagram */}
            <div className="flex flex-wrap gap-2 items-center mb-8 p-4 bg-slate-900 rounded-2xl border border-slate-800">
              {PIPELINE_STAGES.map((stage, idx) => (
                <div key={stage.status} className="flex items-center gap-2">
                  <button
                    onClick={() => setActiveStage(activeStage === stage.status ? null : stage.status)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${activeStage === stage.status
                        ? `bg-gradient-to-r ${stage.color} border-transparent text-white shadow-lg scale-105`
                        : `bg-slate-800 ${stage.borderColor} text-slate-400 hover:text-white hover:border-slate-500`
                      }`}
                  >
                    <span>{stage.icon}</span>
                    <span className="hidden sm:inline">{stage.label}</span>
                    <span className="sm:hidden">{idx + 1}</span>
                  </button>
                  {idx < PIPELINE_STAGES.length - 1 && (
                    <span className="text-slate-600 text-xs">→</span>
                  )}
                </div>
              ))}
            </div>

            {/* Stage Cards */}
            <div className="space-y-4">
              {PIPELINE_STAGES.map((stage, idx) => (
                <div key={stage.status} className={`rounded-2xl border transition-all duration-300 overflow-hidden ${stage.borderColor} ${activeStage === stage.status ? 'shadow-2xl' : ''}`}>
                  <button
                    onClick={() => setActiveStage(activeStage === stage.status ? null : stage.status)}
                    className={`w-full p-5 text-left flex items-center gap-4 transition-all ${activeStage === stage.status
                        ? `bg-gradient-to-r ${stage.color}`
                        : 'bg-slate-800/50 hover:bg-slate-800/70'
                      }`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold shrink-0 ${activeStage === stage.status ? 'bg-white/20' : 'bg-slate-700'}`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xl">{stage.icon}</span>
                        <h3 className="font-bold text-white text-base">{stage.label}</h3>
                        <code className={`text-[10px] px-2 py-0.5 rounded-full ${activeStage === stage.status ? 'bg-white/20 text-white/80' : 'bg-slate-700 text-slate-400'}`}>
                          status: {stage.status}
                        </code>
                      </div>
                      <p className={`text-xs mt-1 ${activeStage === stage.status ? 'text-white/60' : 'text-slate-500'}`}>
                        Performed by: {stage.whoActs}
                      </p>
                    </div>
                    <span className={`text-slate-400 text-xl transition-transform duration-200 shrink-0 ${activeStage === stage.status ? 'rotate-90' : ''}`}>›</span>
                  </button>

                  {activeStage === stage.status && (
                    <div className="p-6 bg-slate-900/80 border-t border-slate-700/50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Steps */}
                        <div className="md:col-span-2">
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <span>📌</span> What happens at this stage
                          </h4>
                          <div className="space-y-3">
                            {stage.whatHappens.map((item, i) => (
                              <div key={i} className="flex items-start gap-3">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${stage.dotColor.replace('bg-', 'bg-') + '/20'} border ${stage.borderColor}`}>
                                  <span className={`text-[9px] ${stage.textColor}`}>{i + 1}</span>
                                </div>
                                <p className={`text-sm leading-relaxed ${item.startsWith('  →') ? 'text-slate-500 pl-2' : 'text-slate-300'}`}>
                                  {item}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Sidebar info */}
                        <div className="space-y-4">
                          <div className={`p-4 rounded-xl border ${stage.borderColor} bg-slate-800/50`}>
                            <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide">Performed by</p>
                            <p className="text-sm font-semibold text-white">{stage.whoActs}</p>
                          </div>
                          <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10">
                            <p className="text-xs text-emerald-400 mb-1 uppercase tracking-wide">Stage Outcome</p>
                            <p className="text-sm font-semibold text-white">{stage.outcome}</p>
                          </div>
                          <div className="p-4 rounded-xl border border-slate-700 bg-slate-800/50">
                            <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide">DB Status Value</p>
                            <code className="text-sm text-cyan-400 font-mono">{stage.status}</code>
                          </div>
                          {idx < PIPELINE_STAGES.length - 1 && (
                            <button
                              onClick={() => setActiveStage(PIPELINE_STAGES[idx + 1].status)}
                              className="w-full p-3 rounded-xl border border-slate-700 text-sm text-slate-400 hover:text-white hover:border-slate-500 transition-all text-left flex items-center gap-2"
                            >
                              <span>Next:</span>
                              <span className="font-medium text-white">{PIPELINE_STAGES[idx + 1].label}</span>
                              <span className="ml-auto">→</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── GLOSSARY ────────────────────────────────────── */}
        {activeSection === 'glossary' && (
          <div className="max-w-4xl mx-auto p-8">
            <h2 className="text-2xl font-bold mb-2">📖 Quick Glossary</h2>
            <p className="text-slate-400 mb-8">Key terms used across the Doxez CRM system.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {GLOSSARY.map((item) => (
                <div key={item.term} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-colors">
                  <h3 className="font-bold text-cyan-400 mb-1 font-mono text-sm">{item.term}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{item.def}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}